import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BadgeCheck } from 'lucide-react';
import { APP_CONFIG } from '@/config/app';
import { SEO_CONFIG } from '@/config/seo';
import { LandingHeader } from '@/components/landing/LandingHeader';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { Container, KeystoneMark } from '@/components/g5';
import { AuraButton } from '@/components/aura';
import { EditorMock, MemberSiteMock, AnalyticsMock, DirectoryMock, ReportMock, ProductMockStyles } from '@/components/landing/ProductMocks';
// Static constants, not the usePricingData hook: this is the highest-traffic
// page and a headline figure does not justify a Supabase round-trip.
import { PRICING, LENDER_PLATFORM } from '@/constants/pricingData';

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const SERIF = "var(--g5-serif)";
const ROYAL = '#2A0A58', ROYAL_INK = '#F4EEFF';

function Eyebrow({ children, onDark }: { children: React.ReactNode; onDark?: boolean }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontFamily: MONO, fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: onDark ? 'rgba(234,217,255,0.85)' : 'var(--g5-key)' }}><KeystoneMark size={12} color={onDark ? '#EAD9FF' : 'var(--g5-key)'} />{children}</span>;
}

function MediaRow({ eyebrow, title, body, visual, onLearn, reverse }: { eyebrow: string; title: string; body: string; visual: React.ReactNode; onLearn: () => void; reverse?: boolean }) {
  return (
    <div className="lp-media grid items-center gap-12 md:grid-cols-2 md:gap-16">
      <div className={reverse ? 'md:order-2' : ''}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h3 className="mt-3.5 font-display text-2xl font-extrabold tracking-tight md:text-[2.1rem] md:leading-[1.08]" style={{ color: 'var(--g5-ink)' }}>{title}</h3>
        <p className="mt-3.5 max-w-md leading-relaxed" style={{ color: 'var(--g5-sub)' }}>{body}</p>
        <div className="mt-6"><AuraButton variant="soft" size="sm" onClick={onLearn}>Learn more <ArrowRight className="h-4 w-4" /></AuraButton></div>
      </div>
      <div className={`win-wrap ${reverse ? 'md:order-1' : ''}`}>{visual}</div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const title = `${APP_CONFIG.name} — ${APP_CONFIG.tagline}`;
  const canonicalUrl = SEO_CONFIG.baseUrl;
  const ink = 'var(--g5-ink)', sub = 'var(--g5-sub)', accent = 'var(--g5-accent)', line = 'var(--g5-line)', card = 'var(--g5-card)', soft = 'var(--g5-soft)';

  const orgSchema = { '@context': 'https://schema.org', '@type': 'Organization', name: SEO_CONFIG.siteName, url: SEO_CONFIG.baseUrl, logo: SEO_CONFIG.defaultOgImage, description: APP_CONFIG.description, sameAs: [] };
  const webSiteSchema = { '@context': 'https://schema.org', '@type': 'WebSite', name: SEO_CONFIG.siteName, url: SEO_CONFIG.baseUrl, description: APP_CONFIG.description };

  // Proof signals surfaced high (Trust & Authority order).
  const proofStats = [
    'Built by a licensed NJ Certified General appraiser',
    'Narrative + form-style reports',
    '21 NJ counties',
    'Every report independently verifiable',
    'Free during beta',
  ];
  const diff = [
    { t: 'Live data fields', d: 'Drop the address, GLA, or opinion of value into the prose. It fills from the file and stays in sync.' },
    { t: 'Real components', d: 'Insert comparable grids, market charts, photos, and maps, generated from your data rather than pasted in.' },
    { t: 'Narrative templates', d: 'Start from polished, defensible language in your own voice, then edit. Never face a blank page.' },
  ];
  return (
    <>
      <Helmet defer={false}>
        <title>{title}</title>
        <meta name="description" content={APP_CONFIG.description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={APP_CONFIG.description} />
        <meta property="og:site_name" content={SEO_CONFIG.siteName} />
        <meta property="og:locale" content={SEO_CONFIG.locale} />
        <meta property="og:image" content={SEO_CONFIG.defaultOgImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={APP_CONFIG.description} />
        <meta name="twitter:image" content={SEO_CONFIG.defaultOgImage} />
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webSiteSchema)}</script>
      </Helmet>

      <ProductMockStyles />
      <div className="ashlar-public flex min-h-screen flex-col">
        <LandingHeader />
        <main className="flex-1">

          {/* HERO */}
          <section className="relative overflow-hidden">
            <Container className="grid items-center gap-12 py-16 md:grid-cols-[0.82fr_1.18fr] md:py-24">
              <div>
                <Eyebrow>Private appraisal work · New Jersey</Eyebrow>
                <h1 className="mt-5 font-display text-[2.6rem] font-extrabold leading-[1.02] tracking-tight md:text-[4.1rem]" style={{ color: ink }}>
                  Your report writes itself <span style={{ color: accent }}>from your data.</span>
                </h1>
                <p className="mt-5 max-w-md text-lg leading-relaxed" style={{ color: sub }}>
                  Estate, divorce, tax appeal, litigation. Ashlar&rsquo;s narrative writer pulls your
                  appraisal data straight into the prose, so a finished, defensible report is minutes
                  of editing. Your own website and a stream of private-work clients come with it.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <AuraButton onClick={() => navigate('/login?tab=signup')}>Start building — free</AuraButton>
                  <AuraButton variant="soft" onClick={() => navigate('/find-an-appraiser')}>Find an appraiser <ArrowRight className="h-4 w-4" /></AuraButton>
                </div>
              </div>
              <div className="win-wrap relative"><EditorMock /></div>
            </Container>
            {/* trust strip */}
            <div style={{ borderTop: `1px solid ${line}` }}>
              <Container className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 py-4">
                {['Built by a licensed NJ appraiser', 'Narrative reports', '21 NJ counties', 'Free during beta'].map((t) => (
                  <span key={t} style={{ fontFamily: MONO, fontSize: 11.5, letterSpacing: '0.04em', color: sub }}>{t}</span>
                ))}
              </Container>
            </div>
          </section>

          {/* PATH FORK — explicit "I am a…" (Enterprise Gateway) */}
          <section>
            <Container className="grid gap-4 py-16 md:grid-cols-2 md:py-20">
              <button type="button" onClick={() => navigate('/for-appraisers')} className="lp-fork rounded-[20px] p-7 text-left md:p-8" style={{ background: card, border: `1px solid ${line}` }}>
                <Eyebrow>For appraisers</Eyebrow>
                <h3 className="mt-3 font-display text-xl font-extrabold tracking-tight md:text-2xl" style={{ color: ink }}>Write faster. Get found.</h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed" style={{ color: sub }}>The narrative platform, your own website, and a stream of private-work leads. Built by an appraiser who has written the report.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-display text-sm font-bold" style={{ color: accent }}>Explore the platform <ArrowRight className="h-4 w-4" /></span>
              </button>
              <button type="button" onClick={() => navigate('/find-an-appraiser')} className="lp-fork rounded-[20px] p-7 text-left md:p-8" style={{ background: card, border: `1px solid ${line}` }}>
                <Eyebrow>Need an appraisal?</Eyebrow>
                <h3 className="mt-3 font-display text-xl font-extrabold tracking-tight md:text-2xl" style={{ color: ink }}>Find a licensed NJ appraiser.</h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed" style={{ color: sub }}>Estate, divorce, tax appeal, and more. Tell us what you need and we&rsquo;ll match you to the right appraiser near you.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-display text-sm font-bold" style={{ color: accent }}>Request an appraisal <ArrowRight className="h-4 w-4" /></span>
              </button>
            </Container>
          </section>

          {/* FOUNDER NOTE — first-person, verifiable. Serif = document voice. */}
          <section style={{ background: soft }}>
            <Container className="flex flex-col items-center gap-7 py-20 text-center md:py-24">
              <KeystoneMark size={22} />
              <p className="max-w-2xl text-xl leading-relaxed md:text-2xl md:leading-relaxed" style={{ fontFamily: SERIF, color: ink }}>
                I write these reports myself. Every tool in Ashlar exists because something in my own
                workflow was slower than it needed to be.
              </p>
              <div className="text-sm" style={{ color: sub }}>
                <span className="font-display font-bold" style={{ color: ink }}>Founder, Ashlar</span> · Certified General appraiser, New Jersey
              </div>
              <div className="mt-1 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5">
                {proofStats.map((t) => (
                  <span key={t} className="inline-flex items-center gap-2" style={{ fontFamily: MONO, fontSize: 11.5, letterSpacing: '0.03em', color: sub }}>
                    <BadgeCheck className="h-3.5 w-3.5" style={{ color: 'var(--g5-key)' }} />{t}
                  </span>
                ))}
              </div>
            </Container>
          </section>

          {/* DIFFERENTIATOR — deep-royal contrast band */}
          <section className="relative overflow-hidden" style={{ background: ROYAL, color: ROYAL_INK }}>
            <Container className="py-24 md:py-32">
              <div className="max-w-2xl">
                <Eyebrow onDark>Not a blank page</Eyebrow>
                <h2 className="mt-3.5 font-display text-3xl font-extrabold tracking-tight md:text-[2.6rem] md:leading-[1.05]">The report assembles from your file.</h2>
                <p className="mt-3.5 max-w-md leading-relaxed" style={{ color: 'rgba(244,238,255,0.74)' }}>The narrative editor is wired to your appraisal data, so you spend your time on judgment instead of typing.</p>
              </div>
              <div className="mt-11 grid gap-4 md:grid-cols-3">
                {diff.map((x) => (
                  <div key={x.t} className="rounded-[20px] p-6" style={{ background: 'rgba(244,238,255,0.06)', border: '1px solid rgba(244,238,255,0.14)' }}>
                    <h3 className="font-display text-lg font-extrabold">{x.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed" style={{ color: 'rgba(244,238,255,0.72)' }}>{x.d}</p>
                  </div>
                ))}
              </div>
            </Container>
          </section>

          {/* THE DELIVERABLE — ReportMock, serif document voice */}
          <section>
            <Container className="grid items-center gap-12 py-24 md:grid-cols-[1.08fr_1fr] md:gap-16 md:py-32">
              <div>
                <Eyebrow>The deliverable</Eyebrow>
                <h2 className="mt-3.5 font-display text-3xl font-extrabold tracking-tight md:text-[2.6rem] md:leading-[1.05]" style={{ color: ink }}>What your client actually receives.</h2>
                <p className="mt-4 max-w-md leading-relaxed" style={{ color: sub }}>
                  Not a form dump. A defensible, court-ready narrative report in your own voice. Every report carries a QR that lets an attorney, court, or lender confirm on the spot that it&rsquo;s authentic and unaltered.
                </p>
                <p className="mt-5 max-w-md border-l-2 pl-4 text-[15px] leading-relaxed" style={{ fontFamily: SERIF, color: ink, borderColor: 'var(--g5-key)' }}>
                  &ldquo;The approach reconciles to an opinion of value of $612,000.&rdquo;
                </p>
                <div className="mt-6"><AuraButton variant="soft" size="sm" onClick={() => navigate('/features')}>See a full report <ArrowRight className="h-4 w-4" /></AuraButton></div>
              </div>
              <div className="win-wrap"><ReportMock /></div>
            </Container>
          </section>

          {/* TOOLS */}
          <section style={{ background: soft }}>
            <Container className="flex flex-col gap-24 py-24 md:py-32">
              <MediaRow eyebrow="Your own website" title="A site that brings the work to you." body="Every member gets a managed, SEO-ready website in their own brand, with a service-area map, instant quote requests, and trust badges. Leads land in your inbox instead of a competitor's." visual={<MemberSiteMock />} onLearn={() => navigate('/features')} />
              <MediaRow reverse eyebrow="Business analytics" title="See exactly how the practice runs." body="Turnaround, revenue, and completion trends at a glance. The numbers you need for tax time and for growing the business." visual={<AnalyticsMock />} onLearn={() => navigate('/features')} />
            </Container>
          </section>

          {/* FIND AN APPRAISER */}
          <section style={{ background: soft }}>
            <Container className="grid items-center gap-12 py-24 md:grid-cols-[1fr_1.05fr] md:py-32">
              <div>
                <Eyebrow>The front door</Eyebrow>
                <h2 className="mt-3.5 font-display text-3xl font-extrabold tracking-tight md:text-[2.6rem] md:leading-[1.05]" style={{ color: ink }}>Get found by the people who need you.</h2>
                <p className="mt-4 max-w-md leading-relaxed" style={{ color: sub }}>Homeowners, attorneys, and agents come to Ashlar to request a licensed New Jersey appraiser. We match each request to the right one, and the lead lands in their inbox.</p>
                <div className="mt-6 flex gap-3">
                  <AuraButton onClick={() => navigate('/login?tab=signup')}>List your practice</AuraButton>
                  <AuraButton variant="soft" onClick={() => navigate('/get-found')}>How leads reach you <ArrowRight className="h-4 w-4" /></AuraButton>
                </div>
              </div>
              <div className="win-wrap"><DirectoryMock /></div>
            </Container>
          </section>

          {/* PRICE — the wedge against lender-form platforms. Centered, no mock:
              a deliberate break from the MediaRow rhythm above it. */}
          <section>
            <Container className="flex flex-col items-center gap-4 py-20 text-center md:py-24">
              <Eyebrow>What it costs</Eyebrow>
              <h2 className="max-w-2xl font-display text-3xl font-extrabold tracking-tight md:text-[2.6rem] md:leading-[1.05]" style={{ color: ink }}>
                Lender-form platforms start at ${LENDER_PLATFORM.floorAnnual.toLocaleString()} a year.
              </h2>
              <p className="max-w-lg leading-relaxed" style={{ color: sub }}>
                Ashlar&rsquo;s unlimited plan is ${PRICING.unlimited.annual.toLocaleString()}. File a
                handful of reports a month and you pay ${PRICING.creditBase} per report instead, with
                no annual floor. Private work is lower volume than lender work, and the pricing should
                reflect that.
              </p>
              <div className="mt-2">
                <AuraButton variant="soft" onClick={() => navigate('/pricing')}>See your cost per report <ArrowRight className="h-4 w-4" /></AuraButton>
              </div>
            </Container>
          </section>

          {/* CLOSE */}
          <section>
            <Container className="flex flex-col items-center gap-5 py-28 text-center md:py-36">
              <KeystoneMark size={34} />
              <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-[2.6rem] md:leading-[1.05]" style={{ color: ink }}>Lay the first stone.</h2>
              <p style={{ color: sub }}>Set up your practice in an afternoon. Free during beta.</p>
              <div className="mt-1 flex gap-3">
                <AuraButton onClick={() => navigate('/login?tab=signup')}>Start building — free</AuraButton>
                <AuraButton variant="soft" onClick={() => navigate('/pricing')}>See pricing <ArrowRight className="h-4 w-4" /></AuraButton>
              </div>
            </Container>
          </section>
        </main>
        <LandingFooter />
      </div>

      <style>{`
        .lp-card, .lp-fork { transition: transform .18s ease, border-color .18s ease; }
        .lp-card:hover, .lp-fork:hover { transform: translateY(-3px); border-color: color-mix(in srgb, var(--g5-key) 55%, var(--g5-line)); }
        .lp-fork { cursor: pointer; }
        @media (prefers-reduced-motion: reduce) { .lp-card, .lp-fork { transition: none; } }
      `}</style>
    </>
  );
}
